import React, {useCallback, useRef, useState} from 'react'
import {ArrUtil} from 'yougile-util'
import {User} from 'yougile-model'
import {Accounts} from '@client/accounts'
import {AvatarGroup} from '@components/common/base/avatar'
import {RingColor} from '@components/common/base/common/ring'
import {Dropdown} from '@components/common/dropdown/dropdown'
import {DropdownContent} from '@components/common/dropdown/dropdown-content'
import {UserAvatar} from '@components/user-avatar'

interface AllUserListProps {
  title: string
  users: User[]
  userInfo?: Record<string, number> // userId -> displayed number
}

const AllUserList = ({title, users, userInfo}: AllUserListProps) => {
  const uniqUsers = ArrUtil.unique(users.filter(Boolean), u => u.id)
  const usersWithNames = uniqUsers.map(
    user => ({user, name: Accounts.getName(user.id) ?? ''} as const)
  )

  usersWithNames.sort((a, b) =>
    a.name.toLowerCase() > b.name.toLowerCase() ? 1 : -1
  )

  return (
    <DropdownContent
      className="max-h-208"
      disableHeaderMargin={true}
      bodyScroll={true}
      header={`${title} (${users.length})`}
    >
      <div className="px-8">
        {usersWithNames.map(u => (
          <div key={u.user.id} className="h-32 flex items-center">
            <UserAvatar user={u.user} size={20} />
            <div className="ml-4">{u.name}</div>
            {userInfo?.[u.user.id] && (
              <div className="flex-grow text-right text-third-old text-12 pl-8">
                {userInfo[u.user.id]}
              </div>
            )}
          </div>
        ))}
      </div>
    </DropdownContent>
  )
}

interface AggregatedUsersProps {
  title: string
  users: User[]
  className?: string
  userInfo?: Record<string, number> // userId -> displayed number
  outlineColor?: RingColor
}

export const AggregatedUsers = (props: AggregatedUsersProps) => {
  const ref = useRef(null)
  const [visible, setVisible] = useState(false)
  const open = useCallback(() => setVisible(true), [setVisible])
  const close = useCallback(() => setVisible(false), [setVisible])

  return props.users.length ? (
    <>
      <AvatarGroup
        ref={ref}
        userIds={props.users.map(x => x.id)}
        size={16}
        outlineColor={props.outlineColor}
        outlineSize={2}
        hideStatus
        className={props.className}
        onClick={open}
        refOnFirst
      />
      {visible && (
        <Dropdown
          reference={ref}
          content={<AllUserList {...props} />}
          visible
          onVisibleChange={close}
        />
      )}
    </>
  ) : null
}
